import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React from "react";
import { useTranslation } from "react-i18next";
import { addCoordinatorCommentsOnDailyReport } from "services/caseServices";
import { CaseCreation, DailyReport, Roles } from "types";
import { feedbackIcon } from "utils/constant";
import { errorHandlerOnForm, hasRole } from "utils/helper";

type Props = {
  selectedCase?: CaseCreation;
  selectedDailyReport?: DailyReport;
  fetchData: () => void;
};

const DailyReportFeedbackSection: React.FC<Props> = ({
  selectedCase,
  selectedDailyReport,
  fetchData,
}) => {
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);
  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isDirector = hasRole(loginData?.roles, [Roles.prosecutionDirector]);

  const [newComment, setNewComment] = React.useState<string>("");
  if (!selectedDailyReport) return <div className="flex-2"></div>;
  return (
    <div className="flex-2">
      <h1 className="text-black font-semibold text-lg flex flex-row gap-2 items-center mb-2">
        <span className="text-[#AA232F] text-xl">{feedbackIcon}</span>
        <span className="tracking-wide">Daily Report Feedback Section</span>
      </h1>

      {selectedDailyReport &&
        selectedCase &&
        selectedCase?.CoordinatorCommentDailyReport?.length === 0 && (
          <div>No feedback found</div>
        )}
      <div className="flex flex-col gap-4 mt-4 justify-between h-[60vh] w-full">
        <div className="flex flex-col overflow-y-auto gap-3 pr-1">
          {selectedCase?.CoordinatorCommentDailyReport &&
          selectedCase?.CoordinatorCommentDailyReport.length > 0 ? (
            selectedCase.CoordinatorCommentDailyReport.map((comment, index) => (
              <div
                key={index}
                className={`flex flex-row items-start gap-3 rounded-lg shadow-sm border border-gray-200 px-4 py-3 ${
                  index % 2 === 0 ? "bg-[#F7F7F7]" : "bg-white"
                }`}
                style={{ overflowWrap: "break-word" }}
              >
                {/* Avatar or role icon */}
                <div className="flex flex-col items-center justify-center mr-2">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      comment.role === "coordinator"
                        ? "bg-[#AA232F] text-white"
                        : "bg-gray-300 text-gray-700"
                    } font-bold text-sm shadow-md`}
                  >
                    {comment.created_by?.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-[10px] text-gray-500 mt-1 capitalize">
                    {comment.created_by}
                  </span>
                </div>
                <div className="flex-1">
                  <div className="flex flex-row gap-2 items-center mb-1">
                    <span className="text-xs text-gray-800">
                      {comment?.created_at &&
                        new Date(comment?.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed mb-1">
                    {comment?.comment}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-gray-800 py-8">
              No feedback yet.
            </div>
          )}
        </div>
        {(isCoordinator || isProsecutor) && selectedDailyReport && (
          <div
            style={{
              boxShadow: "rgb(217 217 217 / 90%) 0px -6px 7px 0px",
            }}
            className="px-6 py-3 w-full bg-white"
          >
            <p>
              {isCoordinator ? "Write Feedback" : "Write Response Back here"}
            </p>
            <div className="flex flex-col mt-2">
              <textarea
                rows={4}
                className="w-full bg-[#F2F2F2] px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#AA232F] text-sm resize-none"
                placeholder="Write your feedback here..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
              />
              <div className="text-right mt-2">
                <button
                  className="bg-[#212121] text-white text-xs px-4 py-1 rounded-md"
                  onClick={() => {
                    if (!newComment.trim()) {
                      showTooltip(t("please_enter_a_comment"), "error");
                      return;
                    }

                    addCoordinatorCommentsOnDailyReport({
                      daily_report: selectedDailyReport?.id,
                      case: selectedCase?.id,
                      role: isCoordinator
                        ? "coordinator"
                        : isDirector
                          ? "director"
                          : "deputy",
                      comment: newComment.trim(),
                      created_by: loginData?.full_name || "",
                    })
                      .then(() => {
                        setNewComment("");
                        fetchData();
                        showTooltip(t("comment_added_successfully"), "success");
                      })
                      .catch((error) => {
                        showTooltip(
                          errorHandlerOnForm(error) || "Error adding comment:",
                          "error",
                        );
                      });
                  }}
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyReportFeedbackSection;
